import { useFormikContext } from "formik";
import _ from "lodash";
import React from "react";
import { useSearchParents } from "src/generated/hooks/form-service/queries";
import { canonicalPhoneNumber } from "src/services/format";
import { isValidEmail, isValidPhoneNumber } from "src/services/formValidations";
import { ParentProfileFormType } from "../schemas";

const SEARCH_DEBOUNCE_MS = 350;
const RESULT_LIMIT = 10;

/**
 * Drives the possible-parent-duplicates search from the live parent form values.
 * Email and phone are searched independently so a parent matching either contact is
 * flagged (not just the first one entered); results are merged, de-duped, and sorted
 * alphabetically. Purely informational for the user.
 */
export function useParentDuplicateSearch(organizationId: string) {
  const { values } = useFormikContext<ParentProfileFormType>();
  const email = (values.email_address ?? "").trim();
  const phone = (values.phone_number ?? "").trim();

  const emailValid = isValidEmail(email);
  const phoneValid = isValidPhoneNumber(phone);
  const isQualified = emailValid || phoneValid;

  const emailSearch = emailValid ? email : "";
  // Phone is stored in E.164; search with the same canonical form so it matches.
  const phoneSearch = phoneValid ? canonicalPhoneNumber(phone) : "";

  const [debouncedEmail, setDebouncedEmail] = React.useState(emailSearch);
  const [debouncedPhone, setDebouncedPhone] = React.useState(phoneSearch);
  const debounce = React.useMemo(
    () =>
      _.debounce((nextEmail: string, nextPhone: string) => {
        setDebouncedEmail(nextEmail);
        setDebouncedPhone(nextPhone);
      }, SEARCH_DEBOUNCE_MS),
    []
  );
  React.useEffect(() => {
    debounce(emailSearch, phoneSearch);
    return () => debounce.cancel();
  }, [emailSearch, phoneSearch, debounce]);

  const emailEnabled = !!organizationId && debouncedEmail.length >= 3;
  const phoneEnabled = !!organizationId && debouncedPhone.length >= 3;

  const emailQuery = useSearchParents(
    {
      query: {
        organizationId,
        search: debouncedEmail,
        limit: RESULT_LIMIT,
        offset: 0,
      },
    },
    undefined,
    { enabled: emailEnabled }
  );
  const phoneQuery = useSearchParents(
    {
      query: {
        organizationId,
        search: debouncedPhone,
        limit: RESULT_LIMIT,
        offset: 0,
      },
    },
    undefined,
    { enabled: phoneEnabled }
  );

  const matches = React.useMemo(() => {
    const combined = [
      ...(emailEnabled ? emailQuery.data?.data.parents ?? [] : []),
      ...(phoneEnabled ? phoneQuery.data?.data.parents ?? [] : []),
    ];
    return _.sortBy(_.uniqBy(combined, "id"), [
      (parent) => (parent.first_name ?? "").toLowerCase(),
      (parent) => (parent.last_name ?? "").toLowerCase(),
    ]);
  }, [emailEnabled, phoneEnabled, emailQuery.data, phoneQuery.data]);

  const isError = emailQuery.isError || phoneQuery.isError;
  // A contact is "settled" when it has nothing to search or its query has returned.
  const emailSettled = !emailEnabled || emailQuery.data !== undefined;
  const phoneSettled = !phoneEnabled || phoneQuery.data !== undefined;
  const termsSettled =
    emailSearch === debouncedEmail && phoneSearch === debouncedPhone;
  const isFetching =
    (emailEnabled && emailQuery.isFetching) ||
    (phoneEnabled && phoneQuery.isFetching);

  const hasSearched =
    isQualified && termsSettled && emailSettled && phoneSettled;
  const isPending = isQualified && (!termsSettled || isFetching);

  return {
    isQualified,
    isLoading: isFetching,
    isError,
    hasSearched,
    isPending,
    matches,
    fullName: [values.first_name, values.last_name].filter(Boolean).join(" "),
  };
}
